/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type ProductModel } from '@/api/types/products-types';
import { useFeatureFlag } from '@/features/globalConfig';
import { useProductReviews } from '@/features/reviews/api/getProductReviews';
import { useLocale } from '@/hooks/useLocale';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import Head from 'next/head';
import {
  generateAggregateRating,
  generateReviewsList,
} from '@/features/reviews/utils/generateReviewsSchema';
import { useMemo } from 'react';

type CustomReviewsMetaDataProps = {
  product: ProductModel | undefined;
};

export const CustomReviewsMetaData = ({ product }: CustomReviewsMetaDataProps) => {
  const gcpUrlEnabled = useFeatureFlag('SEO_GCP_URL_ENABLED') === 'true';

  const locale = useLocale();

  const skuNumber = product?.itemId ?? '';
  const productName = product?.itemDescription;

  const { data: productReviews } = useProductReviews({
    country: getCountryFromLocale(locale),
    customerType: 'B2C',
    skuId: skuNumber,
    enabled: !!skuNumber,
    gcpUrlEnabled,
  });

  const aggregateRatingSchema: string = useMemo(() => {
    if (!product || !productReviews) {
      return '';
    }

    return JSON.stringify({
      '@context': 'https://schema.org/',
      '@id': product.productDetailsPageUrl,
      aggregateRating: {
        ...generateAggregateRating(productReviews),
        itemReviewed: {
          '@type': 'IndividualProduct',
          name: productName,
        },
      },
    });
  }, [product, productReviews, productName]);

  const reviewsSchema: string = useMemo(() => {
    if (!product || !productReviews) {
      return '';
    }

    return JSON.stringify({
      review: generateReviewsList(productReviews),
      '@context': 'https://schema.org/',
      '@id': product.productDetailsPageUrl,
    });
  }, [product, productReviews]);

  return (
    <Head>
      <script
        key={'aggregateRatingSchema'}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: aggregateRatingSchema,
        }}
      />
      <script
        key={'customerReviewsSchema'}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: reviewsSchema,
        }}
      />
    </Head>
  );
};
